/*
  Product page: variant picking, the gallery, accordions and the sticky bar.

  Variants are rendered into the page as JSON rather than fetched, so
  changing a colour is instant and works offline of any request.

  Add to cart is handled here rather than by Dawn's product-form.js, so the
  cart drawer opens without depending on that script being present.

  The size opens already selected: the size of the variant in the link, or
  the first size that can be bought, as the section renders it. The size
  the shopper last picked herself, on any product, is remembered in the
  browser and selected for her on the next product, as long as that product
  has it in stock in the chosen colour; otherwise the page keeps the size it
  opened with. Only a size she picks is remembered, never one the page
  picked for her.
*/
if (!customElements.get('product-page')) {
  // How many thumbnails to keep visible past the selected one, in whichever
  // direction the shopper is moving, so the strip always shows there is more.
  const THUMB_LOOKAHEAD = 2;

  // Long enough for a smooth scroll to finish, for browsers with no
  // scrollend event.
  const SCROLL_SETTLE_MS = 700;

  // Where the gallery stops being a swipeable row and becomes a grid.
  // Must match the desktop breakpoint in main-product.css.
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  // Where the last size the shopper picked is kept, across products.
  const SIZE_KEY = 'rm-selected-size';

  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.url = this.dataset.url;

      this.variants = this.readVariants();

      /*
        Every photograph and thumbnail, in the product's order, and the
        ones currently shown. With the gallery filtered by colour the two
        differ: the markup carries all of them, hidden or not, and
        mediaItems and thumbs are only the selected colour's. See
        collectVisible.
      */
      this.mediaList = this.querySelector('[data-media-list]');
      this.allMedia = this.mediaList ? Array.from(this.mediaList.children) : [];
      this.thumbStrip = this.querySelector('[data-thumbs]');
      this.allThumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-media-counter]');
      this.filtered = this.allMedia.some((item) => item.hasAttribute('data-media-group'));
      this.activeThumb = 0;
      this.jumpingTo = null;

      this.collectVisible();

      /*
        The size option, marked by the section, and its position among the
        options. Zero when the product has none, such as jewellery, and
        then everything behaves as it always did.
      */
      this.sizeFieldset = this.querySelector('[data-size-option]');
      const sizeInput = this.sizeFieldset ? this.sizeFieldset.querySelector('[data-option-position]') : null;
      this.sizePosition = sizeInput ? Number(sizeInput.dataset.optionPosition) : 0;

      // The sticky bar's buying part, size dropdown and photo: laid out for
      // the phone only, but always in the markup. See syncStickySize and
      // updateStickyImage.
      this.stickyActions = this.querySelector('[data-sticky-actions]');
      this.stickySize = this.querySelector('[data-sticky-size]');
      this.stickySizeValue = this.querySelector('[data-sticky-size-value]');
      this.stickyImage = this.querySelector('[data-sticky-image]');

      this.form = this.querySelector('.product__form');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.submit = this.querySelector('[data-submit]');

      // Two copies of the label on each button, so it can roll on hover, and
      // the sticky bar repeats them again. All are written to; updating only
      // the visible one leaves another saying "Add to cart" under a sold out
      // button.
      this.submitTexts = Array.from(this.querySelectorAll('[data-submit-text]'));

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      this.setupGallery();
      this.setupSticky();

      // The shopper's own size from an earlier product, when this one has it.
      if (this.sizePosition) this.applyRememberedSize();

      // The image the current selection belongs to, so a later change can
      // tell whether it brings a different one.
      const opening = this.previewVariant(this.selectedOptions);
      this.currentMediaId = opening && opening.media_id ? opening.media_id : null;

      /*
        Everything that follows from the selected size, brought in step
        once on arrival: the size label, the stock note beside it, which
        values can be chosen, the price, the button, the id sent with it
        and the sticky bar. Only on products with a size; the rest arrive
        right from the section.
      */
      if (this.sizePosition) this.onVariantChange();

      this.showSelectedMedia();
    }

    readVariants() {
      const script = this.querySelector('[data-variants]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    /* Remembered size ----------------------------------------------------- */

    /*
      Selects the size the shopper last picked, if this product has a size
      by exactly that name and it can be bought in the chosen colour.
      Nothing changes otherwise, and storage that is blocked, in a private
      window for example, simply means nothing is remembered.
    */
    applyRememberedSize() {
      let remembered = null;
      try {
        remembered = localStorage.getItem(SIZE_KEY);
      } catch (error) {
        return;
      }
      if (!remembered) return;

      const input = Array.from(
        this.querySelectorAll(`[data-option-position="${this.sizePosition}"]`)
      ).find((item) => item.value.trim().toLowerCase() === remembered.trim().toLowerCase());

      if (!input || input.checked) return;

      const chosen = this.selectedOptions;
      chosen[this.sizePosition - 1] = input.value;

      const variant = this.findVariant(chosen);
      if (!variant || !variant.available) return;

      input.checked = true;
    }

    rememberSize(value) {
      if (!value) return;
      try {
        localStorage.setItem(SIZE_KEY, value);
      } catch (error) {
        // Storage blocked: the size is simply not remembered.
      }
    }

    /* The one variant matching every chosen option, or nothing. */
    findVariant(chosen) {
      return this.variants.find((item) =>
        item.options.every((value, index) => value === chosen[index])
      );
    }

    /*
      A variant to show while an option is still open: the first one of the
      chosen colour that can be bought, or failing that the first one of it
      at all. Its price and photograph stand for the colour.
    */
    previewVariant(chosen) {
      const matching = this.variants.filter((item) =>
        item.options.every((value, index) => chosen[index] === undefined || value === chosen[index])
      );

      return matching.find((item) => item.available) || matching[0] || null;
    }

    /* Whether the product has a size and none is picked yet. */
    sizeMissing() {
      if (!this.sizePosition) return false;
      return !this.querySelector(`[data-option-position="${this.sizePosition}"]:checked`);
    }

    /*
      The size buttons and the sticky bar's dropdown are one choice shown
      twice. A change of a button runs the whole variant update; a change of
      the dropdown checks the matching button and runs the same update, so
      nothing differs by which of the two the shopper used. Either way, a
      size she picks is remembered for the next product.
    */
    onChange(event) {
      if (event.target.matches('[data-option-position]')) {
        if (Number(event.target.dataset.optionPosition) === this.sizePosition) {
          this.rememberSize(event.target.value);
        }
        this.onVariantChange();
        return;
      }

      if (event.target === this.stickySize) this.pickStickySize();
    }

    pickStickySize() {
      const value = this.stickySize.value;
      const input = Array.from(
        this.querySelectorAll(`[data-option-position="${this.sizePosition}"]`)
      ).find((item) => item.value === value);

      if (!input) return;

      input.checked = true;
      this.rememberSize(input.value);
      this.onVariantChange();
    }

    onClick(event) {
      const toggle = event.target.closest('[data-accordion-toggle]');
      if (toggle) {
        this.toggleAccordion(toggle);
        return;
      }

      const thumb = event.target.closest('[data-thumb]');
      if (thumb) {
        this.showMedia(Number(thumb.dataset.thumb));
        return;
      }

      if (event.target.closest('[data-share]')) {
        this.share(event.target.closest('[data-share]'));
        return;
      }

      // The sticky button is outside the form, so it drives the real one
      // rather than duplicating the submit logic, the size check included.
      if (event.target.closest('[data-sticky-submit]') && this.submit) {
        this.submit.click();
      }
    }

    /* Opening on the right image ------------------------------------------ */

    /*
      A product can be opened on a variant that is not the first one: a
      Google Shopping ad for the pink dress, a filtered collection, a swatch
      on a card.

      With the gallery filtered by colour there is nothing to do: the
      section renders only the selected colour's photographs, so the page
      arrives already showing the right ones, on the phone and on desktop.

      A product that is not filtered, one colour or no photographs linked to
      its variants, keeps the old placement on the phone: the row is moved
      to the selected variant's image. On desktop it is left alone, since
      moving there would mean scrolling the page on arrival.
    */
    showSelectedMedia() {
      if (this.filtered) return;
      if (!this.mediaList || this.mediaItems.length < 2 || DESKTOP.matches) return;

      const variant = this.previewVariant(this.selectedOptions);
      if (!variant || !variant.media_id) return;

      const index = this.mediaItems.findIndex(
        (item) => Number(item.dataset.mediaId) === variant.media_id
      );

      // Already there, which is the common case: nothing to do.
      if (index < 1) return;

      this.showMedia(index, true);
    }

    /* Gallery filtered by colour ------------------------------------------ */

    /*
      Rebuilds the lists of what is on screen from what is not hidden, and
      numbers the visible thumbnails from zero, so a tap on the third one
      shown goes to the third photograph shown whatever colour this is.
    */
    collectVisible() {
      this.mediaItems = this.allMedia.filter((item) => !item.hidden);

      this.thumbs = this.allThumbs.filter((thumb) => {
        const holder = thumb.closest('li');
        return !(holder && holder.hidden);
      });

      this.thumbs.forEach((thumb, position) => {
        thumb.dataset.thumb = position;
      });

      this.updateCounter(0);
    }

    /*
      Shows one colour's photographs and hides the rest, when a new colour
      is picked. Only the hidden attribute changes, so it is instant, and a
      colour's photographs are downloaded the first time it is shown rather
      than with the page.

      The gallery then starts again at that colour's first photograph: on a
      phone the row goes back to the start, and on desktop the page is
      brought back up to the gallery if it had been scrolled past its top,
      so the new colour is what the shopper sees.

      The thumbnails follow the photographs by position, since both lists
      are rendered from the same media in the same order.

      A product that is not filtered keeps the old behaviour: the gallery
      moves to the new colour's image.
    */
    showColour(mediaId) {
      this.updateStickyImage();

      if (!this.filtered) {
        this.showMediaById(mediaId, true);
        return;
      }

      const group = String(mediaId);
      if (!this.allMedia.some((item) => item.dataset.mediaGroup === group)) return;

      this.allMedia.forEach((item, position) => {
        item.hidden = item.dataset.mediaGroup !== group;

        const thumb = this.allThumbs[position];
        const holder = thumb ? thumb.closest('li') : null;
        if (holder) holder.hidden = item.hidden;
      });

      this.collectVisible();
      this.jumpingTo = null;
      this.setActiveThumb(0);

      if (DESKTOP.matches) {
        const first = this.mediaItems[0];
        if (first && first.getBoundingClientRect().top < 0) {
          first.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
        return;
      }

      this.mediaList.scrollTo({ left: 0, behavior: 'instant' });
      if (this.thumbStrip) this.thumbStrip.scrollTo({ left: 0, behavior: 'instant' });
    }

    /*
      "1 / 7" over the gallery on a phone: which photograph of the colour is
      on screen, and how many it has. Hidden when there is only one.
    */
    updateCounter(index) {
      if (!this.counter) return;

      const total = this.mediaItems.length;
      this.counter.hidden = total < 2;

      const current = this.counter.querySelector('[data-media-index]');
      const count = this.counter.querySelector('[data-media-total]');

      if (current) current.textContent = Math.min(index + 1, Math.max(total, 1));
      if (count) count.textContent = total;
    }

    /* Variants ------------------------------------------------------------ */

    /*
      The value checked for each option, by position. An option with
      nothing checked is left undefined.
    */
    get selectedOptions() {
      const chosen = [];

      this.querySelectorAll('[data-option-position]:checked').forEach((input) => {
        chosen[Number(input.dataset.optionPosition) - 1] = input.value;
      });

      return chosen;
    }

    /*
      Everything that follows a change of colour or size.

      With the size still open, which no longer happens on arrival but is
      kept as a safety net, there is no variant to buy yet, so the price
      and photographs come from the colour's first variant, the button
      stays as Add to cart and the id sent with it stays empty; the size
      label keeps its prompt.

      The gallery changes only when the selection brings a different image,
      which in practice means a new colour: every size of a colour shares
      its image, so picking a size never moves the photographs.
    */
    onVariantChange() {
      const chosen = this.selectedOptions;
      const missing = this.sizeMissing();
      const variant = missing ? null : this.findVariant(chosen);
      const shown = variant || this.previewVariant(chosen);

      this.updateOptionLabels(chosen, missing);
      this.updateAvailability(chosen);
      this.syncStickySize();
      this.updatePrice(shown);
      this.updateSubmit(variant, missing);
      this.updateSizeStock(variant, missing);
      this.updateUrl(variant || shown);

      if (!missing) this.clearSizeRequired();

      if (!shown || !shown.media_id) return;

      const mediaChanged = shown.media_id !== this.currentMediaId;
      this.currentMediaId = shown.media_id;

      if (mediaChanged) this.showColour(shown.media_id);
    }

    /*
      The value beside each option's name. The size shows the prompt if
      none is picked, otherwise the size itself.
    */
    updateOptionLabels(chosen, missing) {
      this.querySelectorAll('[data-option-value]').forEach((label) => {
        const position = Number(label.dataset.optionValue);

        if (position === this.sizePosition && missing) {
          label.textContent = this.sizeFieldset.dataset.sizePrompt || '';
          return;
        }

        const value = chosen[position - 1];
        if (value) label.textContent = value;
      });
    }

    /*
      Marks values that cannot be reached from the current selection, with
      only the other options held fixed. An option left open counts as any
      value.
    */
    updateAvailability(chosen) {
      this.querySelectorAll('[data-option-position]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;

        const available = this.variants.some((item) =>
          item.available
            && item.options[position] === input.value
            && item.options.every(
              (value, index) => index === position || chosen[index] === undefined || value === chosen[index]
            )
        );

        input.toggleAttribute('data-unavailable', !available);

        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('product__value--unavailable', !available);
      });
    }

    /*
      The sticky bar copies the size buttons.

      The dropdown holds the same size, or its placeholder while none is
      picked, with the sizes that cannot be bought in the chosen colour
      greyed out, from the marks updateAvailability has just set on the
      buttons.

      The bar's buying part follows: with no size it is the one "Grösse
      wählen" button, with the dropdown invisibly over it; with a size it
      is the small box showing that size beside Add to cart. is-choosing
      switches between the two in the stylesheet.
    */
    syncStickySize() {
      if (!this.stickySize) return;

      const inputs = Array.from(this.querySelectorAll(`[data-option-position="${this.sizePosition}"]`));
      const checked = inputs.find((input) => input.checked);

      Array.from(this.stickySize.options).forEach((option) => {
        if (!option.value) return;
        const input = inputs.find((item) => item.value === option.value);
        option.disabled = Boolean(input && input.hasAttribute('data-unavailable'));
      });

      this.stickySize.value = checked ? checked.value : '';
      if (this.stickySizeValue) this.stickySizeValue.textContent = checked ? checked.value : '';
      if (this.stickyActions) this.stickyActions.classList.toggle('is-choosing', !checked);
    }

    /*
      The sticky bar's photo follows the colour. The new colour's swatch
      photograph is already on the page, so its address is borrowed rather
      than a new one fetched.
    */
    updateStickyImage() {
      if (!this.stickyImage) return;

      const swatch = this.querySelector('.product__option--color input:checked + label img');
      if (swatch) this.stickyImage.src = swatch.currentSrc || swatch.src;
    }

    /*
      The buy column and the sticky card both show a price and a struck
      through original. They are updated together from one variant, so the
      two can never disagree about whether something is on sale.
    */
    updatePrice(variant) {
      if (!variant) return;

      const current = this.querySelector('[data-price-current]');
      const compare = this.querySelector('[data-price-compare]');
      const stickyPrice = this.querySelector('[data-sticky-price]');
      const stickyCompare = this.querySelector('[data-sticky-compare]');

      [current, stickyPrice].forEach((element) => {
        if (!element) return;
        element.textContent = variant.price;
        element.classList.toggle('product__price-item--sale', variant.on_sale);
      });

      [compare, stickyCompare].forEach((element) => {
        if (!element) return;
        element.textContent = variant.on_sale ? variant.compare_at_price : '';
        element.toggleAttribute('hidden', !variant.on_sale);
      });
    }

    /*
      The id sent with the form, and the button's state and label.

      With the size open the id is empty and the button stays enabled, as
      Add to cart: pressing it is how the shopper finds out a size is
      needed, see requireSize. A button that looked disabled would give her
      no way to learn why.
    */
    updateSubmit(variant, missing) {
      if (this.variantInput) this.variantInput.value = variant ? variant.id : '';

      const stickyVariant = this.querySelector('[data-sticky-variant]');
      if (stickyVariant) stickyVariant.textContent = this.selectedOptions.filter(Boolean).join(' / ');

      if (!this.submit) return;

      let label = window.variantStrings.addToCart;

      if (missing) {
        this.submit.disabled = false;
      } else {
        this.submit.disabled = !(variant && variant.available);
        if (!variant) label = window.variantStrings.unavailable;
        else if (!variant.available) label = window.variantStrings.soldOut;
      }

      this.submitTexts.forEach((element) => {
        element.textContent = label;
      });
    }

    /*
      The stock note beside the picked size: "Nur noch 2 verfügbar" in red
      when the variant carries a hand set count, the plain low wording when
      it is low by Shopify's own stock, "Ausverkauft" in red when it cannot
      be bought, and "An Lager" in green otherwise. Hidden while no size is
      picked. The wording is the shop's own, from the section's settings.

      Its size is the stylesheet's: the long few left note is set smaller
      there, by its state, so it fits beside the size chart link with
      nothing measured here.
    */
    updateSizeStock(variant, missing) {
      if (!this.sizeFieldset) return;

      const note = this.sizeFieldset.querySelector('[data-size-stock]');
      if (!note) return;

      if (missing || !variant) {
        note.hidden = true;
        return;
      }

      const labels = this.sizeFieldset.dataset;
      let state = 'in';
      let text = labels.stockIn;

      if (!variant.available) {
        state = 'out';
        text = labels.stockOut;
      } else if (Number(variant.urgency) > 0 && labels.stockLow) {
        state = 'low';
        text = labels.stockLow.replace('[count]', variant.urgency);
      } else if (variant.low && labels.stockLowPlain) {
        state = 'low';
        text = labels.stockLowPlain;
      }

      note.dataset.state = state;
      note.textContent = text || '';
      note.hidden = !text;
    }

    /*
      Add to cart was pressed with no size picked. Nothing is added: the
      prompt turns red, the sizes that can still be bought are outlined in
      red, and the page scrolls smoothly to them. The warning goes as soon
      as a size is picked.
    */
    requireSize() {
      if (!this.sizeFieldset) return;

      this.sizeFieldset.classList.add('is-required');
      this.sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    clearSizeRequired() {
      if (this.sizeFieldset) this.sizeFieldset.classList.remove('is-required');
    }

    updateUrl(variant) {
      if (!variant || !this.url) return;
      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
    }

    /* Gallery ------------------------------------------------------------- */

    setupGallery() {
      if (!this.mediaList || !this.allThumbs.length) return;

      let ticking = false;
      this.mediaList.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.syncThumbs();
            ticking = false;
          });
        },
        { passive: true }
      );
    }

    /*
      Follows the scroll position, for swiping. Suppressed while a jump is
      in flight: tapping the fourth thumbnail scrolls past the second and
      third, and without this the underline is set on each one on the way,
      which looks like the line running along the strip.
    */
    syncThumbs() {
      if (this.jumpingTo !== null) return;

      const centre = this.mediaList.scrollLeft + this.mediaList.clientWidth / 2;

      let active = 0;
      let closest = Infinity;

      this.mediaItems.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft + item.offsetWidth / 2 - centre);
        if (distance < closest) {
          closest = distance;
          active = index;
        }
      });

      this.setActiveThumb(active);
    }

    /* The underline and the counter move together. */
    setActiveThumb(index) {
      this.thumbs.forEach((thumb, position) => {
        thumb.toggleAttribute('aria-current', position === index);
      });

      this.updateCounter(index);
      this.followThumb(index);
    }

    /*
      Keeps a couple of thumbnails visible past the selected one, on the side
      the shopper is heading towards. Measured from rectangles rather than
      offsetLeft: the list is not a positioned ancestor, so offsetLeft is
      relative to something further up the tree and the arithmetic drifts.
    */
    followThumb(index) {
      if (!this.thumbStrip || !this.thumbs.length) return;

      const forward = index >= this.activeThumb;
      this.activeThumb = index;

      const target = forward
        ? Math.min(index + THUMB_LOOKAHEAD, this.thumbs.length - 1)
        : Math.max(index - THUMB_LOOKAHEAD, 0);

      const lead = this.thumbs[target];
      if (!lead) return;

      const strip = this.thumbStrip.getBoundingClientRect();
      const rect = lead.getBoundingClientRect();
      const gutter = 8;

      // One check per edge, so the same code serves both directions.
      if (rect.right > strip.right) {
        this.thumbStrip.scrollBy({ left: rect.right - strip.right + gutter, behavior: 'smooth' });
      } else if (rect.left < strip.left) {
        this.thumbStrip.scrollBy({ left: rect.left - strip.left - gutter, behavior: 'smooth' });
      }
    }

    /*
      instant is passed by a variant change and by the opening placement,
      and left alone by a thumbnail tap. It has to be the string 'instant'
      rather than 'auto': the gallery sets scroll-behavior: smooth in CSS,
      and that wins over 'auto'.
    */
    showMedia(index, instant) {
      const item = this.mediaItems[index];
      if (!item || !this.mediaList) return;

      const behavior = instant ? 'instant' : 'smooth';

      // Set the destination now, and hold off the scroll handler until the
      // gallery has arrived, so the images passed over are never marked.
      this.jumpingTo = index;
      this.setActiveThumb(index);
      this.releaseAfterScroll(instant);

      if (!DESKTOP.matches) {
        // Mobile: a horizontal scroller, so move the strip itself.
        const left = item.offsetLeft - this.mediaList.offsetLeft;
        this.mediaList.scrollTo({ left, behavior });
      } else {
        /*
          Desktop: a vertical grid, so the page moves. start, not nearest:
          nearest brings the bottom edge just into view and leaves the chosen
          colour at the foot of the screen.
        */
        item.scrollIntoView({ behavior, block: 'start' });
      }
    }

    releaseAfterScroll(instant) {
      clearTimeout(this.jumpTimer);

      const release = () => {
        clearTimeout(this.jumpTimer);
        this.jumpingTo = null;
      };

      // An instant jump is over by the next frame, and waiting 700ms for a
      // scroll that already happened would ignore a swipe made in between.
      if (instant) {
        requestAnimationFrame(release);
        return;
      }

      if ('onscrollend' in window) {
        this.mediaList.addEventListener('scrollend', release, { once: true });
      }

      this.jumpTimer = setTimeout(release, SCROLL_SETTLE_MS);
    }

    showMediaById(mediaId, instant) {
      const index = this.mediaItems.findIndex((item) => Number(item.dataset.mediaId) === mediaId);
      if (index > -1) this.showMedia(index, instant);
    }

    /* Accordions ---------------------------------------------------------- */

    toggleAccordion(toggle) {
      const panel = toggle.closest('[data-accordion]');
      if (!panel) return;

      const isOpen = panel.hasAttribute('open');
      panel.toggleAttribute('open', !isOpen);
      toggle.setAttribute('aria-expanded', String(!isOpen));
    }

    /* Sticky bar ---------------------------------------------------------- */

    setupSticky() {
      this.sticky = this.querySelector('[data-sticky]');
      if (!this.sticky || !this.submit) return;

      /*
        isIntersecting alone is not enough. It is false both when the button
        has been scrolled past and when it has not been reached yet, which on
        a phone is true on page load. boundingClientRect.top tells the two
        apart: negative means the button is above the viewport.
      */
      const observer = new IntersectionObserver(
        ([entry]) => {
          const scrolledPast = entry.boundingClientRect.top < 0;
          this.sticky.toggleAttribute('hidden', entry.isIntersecting || !scrolledPast);
        },
        { threshold: 0 }
      );

      observer.observe(this.submit);
    }

    /* Share --------------------------------------------------------------- */

    async share(button) {
      const url = button.dataset.url;

      if (navigator.share) {
        try {
          await navigator.share({ title: document.title, url });
        } catch (error) {
          // Dismissed by the visitor. Nothing to report.
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(url);
        button.classList.add('is-copied');
        setTimeout(() => button.classList.remove('is-copied'), 2000);
      } catch (error) {
        // Clipboard blocked. The address bar still has the URL.
      }
    }

    /* Add to cart --------------------------------------------------------- */

    /*
      The size check comes first, before anything else, so a missing size
      is caught whether or not there is a drawer to open: the form is never
      sent without one.
    */
    async onSubmit(event) {
      if (this.sizeMissing()) {
        event.preventDefault();
        this.requireSize();
        return;
      }

      const drawer = document.querySelector('cart-drawer, cart-notification');

      // With no drawer to open, a normal form post to /cart is the better
      // outcome than intercepting and going nowhere.
      if (!drawer || !this.submit) return;

      event.preventDefault();
      if (this.submit.disabled) return;

      this.submit.setAttribute('aria-busy', 'true');

      const body = new FormData(this.form);
      if (drawer.getSectionsToRender) {
        body.append('sections', drawer.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body,
        });
        const result = await response.json();

        if (result.status) throw new Error(result.description || result.message);

        if (drawer.renderContents) drawer.renderContents(result);
        else window.location.href = window.routes.cart_url;
      } catch (error) {
        // Fall back to the cart page rather than failing silently.
        window.location.href = window.routes.cart_url;
      } finally {
        this.submit.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('product-page', ProductPage);
}
